import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import anime from "animejs"
import classNames from "classnames"

import { CommonTransition } from "@/topic/rogue_4/components/CommonTransition"
import ANIMATION from "../../animation"
import Button from "../../components/Button"
import GameCharacter from "../../components/Character"
import CharacterBoard from "../../components/CharacterBoard"
import CircleButton from "../../components/CircleButton"
import DialogFrame, {
    DialogContent,
    DialogTitle,
} from "../../components/Dialog"
import GAME_EMITTER from "../../components/Emitter"
import {
    useGameBackpackModal,
    useGameGalleryModal,
    useGameHistoryModal,
    useGameItemModal,
    useGameRestartModal,
} from "../../components/Modal/store"
import CHARACTER_DICT from "../../data/character"
import DIALOG_DICT from "../../data/dialog"
import GAME_ITEM_DICT from "../../data/item"
import { useDivRef } from "../../hooks/useDivRef"
import { useEvent } from "../../hooks/useEvent"
import { useSingleEase } from "../../hooks/useSingleEase"
import { useGameProgress, useGameStore } from "../../store"
import { IAnimateRef, IGameDialog, IGameDialogUnit } from "../../type"
import { SFX } from "../../utils/sound"
import { useNextScene } from "../store"

import styles from "./index.module.less"

const DialogScene = React.forwardRef<IAnimateRef>((_, ref) => {
    const nextScene = useNextScene()
    // const [state, setState] = useState(false)

    // dialog store
    const {
        currentDialog,
        currentConversation,
        setCurrentConversation,
        setCurrentDialog,
        choose,
        gainItem,
    } = useGameProgress()
    const gameComplete = useGameStore((state) => state.gameComplete)

    // main frame control
    const [mainFrameActive, setMainFrameActive] = useState(false)

    // main content control
    const [mainContentActive, setMainContentActive] = useState(false)
    const contentRef = useSingleEase(mainContentActive, 500, 500)
    const [text, setText] = useState("")
    const lockNextRef = useRef<boolean>(false)
    const lockAllRef = useRef(false)
    const onTypeEnd = useCallback(() => {
        if (!currentConversation) return
        if (currentConversation.next !== "QUESTION") lockNextRef.current = false
    }, [currentConversation])
    const changeText = useCallback(
        (value: string) => {
            if (text === value) {
                return
            } else {
                lockNextRef.current = true
                setText(value)
            }
        },
        [text]
    )

    // character control
    // TODO 考虑是否用useMemo切换为currentConversation单状态控制
    const [leftActive, setLeftActive] = useState(false)
    const [rightActive, setRightActive] = useState(false)
    const [leftGray, setLeftGray] = useState(true)
    const [rightGray, setRightGray] = useState(true)

    // modal button
    const callBackpackModal = useGameBackpackModal()
    const callItemModal = useGameItemModal()
    const callGalleryModal = useGameGalleryModal()
    const callHistoryModal = useGameHistoryModal()
    const callRestartModal = useGameRestartModal()

    const handleRestart = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            SFX.play("commonClick")
            callRestartModal()
        },
        [callRestartModal]
    )
    const handleHistory = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            SFX.play("commonClick")
            callHistoryModal()
        },
        [callHistoryModal]
    )

    // next
    const lockRef = useRef(false)
    const changeConversation = useEvent((conversation: IGameDialogUnit) => {
        const cb = () => {
            setCurrentConversation(conversation)
            // 中央对话
            changeText(conversation.description)
            // 更新角色
            if (conversation.character === "nymph") {
                setLeftGray(false)
            } else {
                setLeftGray(true)
            }
            if (conversation.character === currentDialog.character) {
                setRightGray(false)
            } else {
                setRightGray(true)
            }
        }
        if (conversation.gainItem) {
            console.log('conversation.gainItem',conversation.gainItem)
            callItemModal(GAME_ITEM_DICT[conversation.gainItem], false, () => {
                gainItem(conversation.gainItem!)
                cb()
            })
        } else {
            cb()
        }

        // TODO 如果是选项，更新按钮状态
        // TODO 如果有获得道具，拉起道具弹窗
    })
    const titleText = useMemo(() => {
        if (!currentConversation) return ""
        const chr = currentConversation?.character
        if (chr === "narration") {
            return ""
        } else if (CHARACTER_DICT[chr]) {
            return CHARACTER_DICT[chr].name
        } else {
            return currentConversation.characterName ?? ""
        }
    }, [currentConversation])
    const changeDialog = useCallback(
        (dialog: IGameDialog, convKey?: string) => {
            lockRef.current = true
            setRightActive(false)
            setMainContentActive(false)
            setMainFrameActive(false)
            setTimeout(() => {
                changeText("")
                setCurrentDialog(dialog)
                setRightActive(true)
                setMainContentActive(true)
                setMainFrameActive(true)
                setTimeout(() => {
                    changeConversation(dialog.dialog[convKey ?? dialog.start])
                    lockRef.current = false
                }, 1000)
            }, 1000)
        },
        [changeConversation, changeText, setCurrentDialog]
    )
    const onChoice = useCallback(
        (choice: {
            key: string
            description: string
            next: string
            gainItem?: string
        }) => {
            if (!currentConversation) return
            choose(currentConversation.key, choice.key)
            if (choice.gainItem) {
                console.log('choice.gainItem',choice.gainItem)
                console.log('choice.gainItem332======',choice)
                callItemModal(GAME_ITEM_DICT[choice.gainItem], false, () => {
                    gainItem(choice.gainItem!)
                    changeConversation(currentDialog.dialog[choice.next])
                })
            } else {
                changeConversation(currentDialog.dialog[choice.next])
            }
        },
        [
            choose,
            currentConversation,
            changeConversation,
            currentDialog,
            callItemModal,
            gainItem,
        ]
    )
    const nextConversation = useCallback(() => {
        if (lockRef.current) return
        if (lockNextRef.current) return
        if (!currentConversation) {
            // changeConversation(currentDialog.dialog[currentDialog.start])
            return
        }
        const next = currentDialog.dialog[currentConversation.next]
        if (next) {
            changeConversation(next)
        } else if (currentConversation.next === "NEXT_DIALOG") {
            // console.log(DIALOG_DICT)
            const nextDialog = DIALOG_DICT[currentDialog.next] as IGameDialog
            if (nextDialog) {
                changeDialog(nextDialog)
            } else {
                nextScene()
            }
        }
    }, [
        changeConversation,
        changeDialog,
        currentConversation,
        currentDialog,
        nextScene,
    ])

    const handleSkip = useCallback(
        (e: React.MouseEvent) => {
            if (lockAllRef.current) return
            SFX.play("commonClick")
            if (!currentConversation) return
            if (lockRef.current) return
            const dialogDict = currentDialog.dialog
            let conv: IGameDialogUnit = currentConversation
            let dialog: IGameDialog = currentDialog
            while (!conv.choice) {
                const nextConv = dialog.dialog[conv.next]
                if (nextConv) conv = nextConv
                else {
                    if (dialog.next === "NEXT_SCENE") {
                        nextScene()
                        return
                    }
                    dialog = DIALOG_DICT[dialog.next]
                    conv = dialog.dialog[dialog.start]
                }
            }
            if (conv.key === currentConversation.key) return
            else if (dialog === currentDialog) {
                // 当前角色对话的选项
                changeConversation(conv)
            } else {
                // 下一角色对话的选项
                changeDialog(dialog, conv.key)
            }
            e.stopPropagation()
        },
        [
            currentDialog,
            currentConversation,
            nextScene,
            changeConversation,
            changeDialog,
        ]
    )

    // bind event
    useEffect(() => {
        const cb = () => {
            if (!lockAllRef.current) nextConversation()
        }
        // FIXME remove this
        // const cb = () => {
        //     choose("01-question", "01a")
        //     choose("02-question", "02a")
        //     choose("03-question", "03a")
        //     choose("04-question", "04a")
        //     choose("05-question", "05a")
        //     choose("06-question", "06")
        //     gainItem("01a")
        //     gainItem("02a")
        //     gainItem("03a")
        //     gainItem("04a")
        //     gainItem("05a")
        //     gainItem("06")
        //     nextScene()
        // }
        GAME_EMITTER.on("NEXT", cb)
        return () => GAME_EMITTER.off("NEXT", cb)
    }, [nextConversation])

    const bgRef = useDivRef()
    const leftCardRef = useDivRef()
    const rightCardRef = useDivRef()
    const buttonsRef = useDivRef()

    const dialogFrameRef = useDivRef()
    const dialogContentRef = useDivRef()
    //需改回
    // useImperativeHandle(
    //     ref,
    //     () => ({
    //         fadeIn: async () => {
    //             lockAllRef.current = true
    //             setMainFrameActive(true)
    //             anime
    //                 .timeline()
    //                 // .add(ANIMATION.in(bgRef, {}, { duration: 1000 }), 0)
    //                 .add(
    //                     {
    //                         begin: () => {
    //                             setMainContentActive(true)
    //                             setLeftActive(true)
    //                             setLeftGray(false)
    //                             setRightActive(true)
    //                         },
    //                     },
    //                     100
    //                 )
    //                 .add(
    //                     {
    //                         begin: () => {
    //                             changeConversation(
    //                                 currentDialog.dialog[currentDialog.start]
    //                             )
    //                             lockAllRef.current = false
    //                         },
    //                     },
    //                     160
    //                 )
    //                 .add(
    //                     ANIMATION.in(
    //                         buttonsRef,
    //                         {},
    //                         {
    //                             duration: 10,
    //                         }
    //                     ),
    //                     200
    //                 )
    //         },
    //         fadeOut: async () => {
    //             const inst = anime.timeline()

    //             inst.add(ANIMATION.out(bgRef, {}, { duration: 10 }), 50)
    //                 .add(ANIMATION.out(buttonsRef, {}, { duration: 5 }), 0)
    //                 .add(
    //                     ANIMATION.out(
    //                         [
    //                             dialogFrameRef,
    //                             dialogContentRef,
    //                             leftCardRef,
    //                             rightCardRef,
    //                         ],
    //                         {},
    //                         { duration: 10 }
    //                     ),
    //                     0
    //                 )

    //                 .add(
    //                     {
    //                         begin: () => {
    //                             // setMainContentActive(true)
    //                             setLeftActive(false)
    //                             setRightActive(false)
    //                         },
    //                     },
    //                     0
    //                 )
    //             return inst.finished
    //         },
    //     }),
    //     [
    //         bgRef,
    //         buttonsRef,
    //         changeConversation,
    //         currentDialog.dialog,
    //         currentDialog.start,
    //         dialogContentRef,
    //         dialogFrameRef,
    //         leftCardRef,
    //         rightCardRef,
    //     ]
    // )
    useImperativeHandle(
        ref,
        () => ({
            fadeIn: async () => {
                lockAllRef.current = true
                setMainFrameActive(true)
                anime
                    .timeline()
                    // .add(ANIMATION.in(bgRef, {}, { duration: 1000 }), 0)
                    .add(
                        {
                            begin: () => {
                                setMainContentActive(true)
                                setLeftActive(true)
                                setLeftGray(false)
                                setRightActive(true)
                            },
                        },
                        1000
                    )
                    .add(
                        {
                            begin: () => {
                                changeConversation(
                                    currentDialog.dialog[currentDialog.start]
                                )
                                lockAllRef.current = false
                            },
                        },
                        1600
                    )
                    .add(
                        ANIMATION.in(
                            buttonsRef,
                            {},
                            {
                                duration: 1000,
                            }
                        ),
                        2000
                    )
            },
            fadeOut: async () => {
                const inst = anime.timeline()

                inst.add(ANIMATION.out(bgRef, {}, { duration: 1000 }), 500)
                    .add(ANIMATION.out(buttonsRef, {}, { duration: 500 }), 0)
                    .add(
                        ANIMATION.out(
                            [
                                dialogFrameRef,
                                dialogContentRef,
                                leftCardRef,
                                rightCardRef,
                            ],
                            {},
                            { duration: 1000 }
                        ),
                        0
                    )

                    .add(
                        {
                            begin: () => {
                                // setMainContentActive(true)
                                setLeftActive(false)
                                setRightActive(false)
                            },
                        },
                        0
                    )
                return inst.finished
            },
        }),
        [
            bgRef,
            buttonsRef,
            changeConversation,
            currentDialog.dialog,
            currentDialog.start,
            dialogContentRef,
            dialogFrameRef,
            leftCardRef,
            rightCardRef,
        ]
    )

    return (
        <div className={styles.dialogScene}>
            <div className={styles.totalBg} ref={bgRef}></div>
            <DialogFrame
                ref={dialogFrameRef}
                className={styles.mainDialog}
                active={mainFrameActive}
                bg={currentDialog.image}
            ></DialogFrame>
            <CharacterBoard
                className={styles.leftBoard}
                ref={leftCardRef}
                type="left"
                characterKey="nymph"
            />
            <CharacterBoard
                className={styles.rightBoard}
                ref={rightCardRef}
                type="right"
                characterKey={currentDialog.character}
            />
            <GameCharacter
                gray={leftGray}
                character={CHARACTER_DICT["nymph"]}
                active={leftActive}
            />
            <GameCharacter
                gray={rightGray}
                character={CHARACTER_DICT[currentDialog.character]}
                active={rightActive}
            />

            <div className={styles.mainContent} ref={dialogContentRef}>
                <DialogTitle
                    className={styles.title}
                    active={mainContentActive}
                    text={titleText}
                ></DialogTitle>
                <DialogContent
                    className={styles.content}
                    text={text}
                    onTypeEnd={onTypeEnd}
                    ref={contentRef}
                ></DialogContent>
            </div>
            <div className={styles.circleButtons} ref={buttonsRef}>
                <CircleButton
                    type="backpack"
                    className={styles.buttonBackpack}
                    onClick={() => {
                        SFX.play("commonClick")
                        callBackpackModal()
                    }}
                />
                {gameComplete && (
                    <CircleButton
                        type="gallery"
                        className={styles.buttonGallery}
                        onClick={() => {
                            SFX.play("commonClick")
                            callGalleryModal()
                        }}
                    />
                )}
            </div>
            <CommonTransition state={currentConversation?.key ?? ""}>
                <div
                    className={classNames(
                        styles.choiceButtonBar,
                        styles.active
                    )}
                >
                    {currentConversation?.choice && (
                        <>
                            {currentConversation.choice.map((c) => (
                                <Button
                                    key={c.key}
                                    className={styles.button}
                                    active={false}
                                    onClick={() => {
                                        onChoice(c)
                                    }}
                                >
                                    {c.description}
                                </Button>
                            ))}
                        </>
                    )}
                </div>
            </CommonTransition>

            <div className={styles.buttonBar}>
                <div
                    className={classNames(styles.button, styles.restart)}
                    onClick={handleRestart}
                ></div>
                <div
                    className={classNames(styles.button, styles.history)}
                    onClick={handleHistory}
                ></div>
                <div
                    className={classNames(styles.button, styles.skip)}
                    onClick={handleSkip}
                ></div>
            </div>
            {/* <ToastContainer active={state}>
                你好，这是个loading过程
            </ToastContainer> */}
        </div>
    )
})
DialogScene.displayName = "DialogScene"

export default DialogScene
